<template>
  <div>
    <v-progress-linear
      class="mt-5 mb-7"
      v-show="isDefaultServiceValueLoading"
      indeterminate
      rounded
    />

    <div v-if="!isDefaultServiceValueLoading">
      <h6 class="text-uppercase grey--text">
        {{ $t('defaultServiceValues.label.serviceType') }}
      </h6>
      <span class="body-2">
        {{ $t(`type.${defaultServiceValue.type}`) }}
      </span>

      <v-row>
        <v-col cols="6" sm="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('defaultServiceValues.label.contractType') }}
          </h6>
          <span class="body-2">
            {{ $t(`paymentTypes.${defaultServiceValue.contract_type}`) }}
          </span>
        </v-col>
        <v-col cols="6" sm="2">
          <h6 class="text-uppercase grey--text">
            {{ $t('defaultServiceValues.label.active') }}
          </h6>
          <v-icon
            :color="defaultServiceValue.is_active ? 'green' : 'red'"
            small
          >
            {{ defaultServiceValue.is_active ? 'mdi-check' : 'mdi-close' }}
          </v-icon>
        </v-col>
        <v-col cols="12" sm="6">
          <h6 class="text-uppercase grey--text">
            {{ $t('defaultServiceValues.label.city') }}
          </h6>
          <span class="body-2">
            {{ defaultServiceValue.city ? defaultServiceValue.city : '---' }}
          </span>
        </v-col>
      </v-row>

      <v-divider class="mt-1" />

      <h4 class="mt-8">
        {{ $t('contract.label.values') }}
      </h4>

      <v-row align="end">
        <v-col cols="6" sm="3">
          <h6 class="text-uppercase grey--text">
            {{ $t('contract.details.price') }}
          </h6>
          <span class="body-2">
            {{
              defaultServiceValue.price
                ? formatMoney(defaultServiceValue.price)
                : '---'
            }}
          </span>
        </v-col>
        <v-col cols="6" sm="3">
          <h6 class="text-uppercase grey--text">
            {{ $t('contract.details.discount') }}
          </h6>
          <span class="body-2">
            {{ defaultServiceValue.discount }}%
          </span>
        </v-col>
        <v-col cols="6" sm="3">
          <h6 class="text-uppercase grey--text">
            {{ $t('contract.details.squareMetersPrice') }}
          </h6>
          <span class="body-2">
            {{
              defaultServiceValue.square_meter_price
                ? formatMoney(defaultServiceValue.square_meter_price)
                : '---'
            }}
          </span>
        </v-col>
        <v-col cols="6" sm="3">
          <h6 class="text-uppercase grey--text">
            {{ $t('contract.details.baseSquareMetersPrice') }}
          </h6>
          <span class="body-2">
            {{ defaultServiceValue.base_square_meters_size }}m²
          </span>
        </v-col>
      </v-row>

      <v-divider class="mt-4" />

      <h4 class="mt-8">
        {{ $t('defaultServiceValues.taxes') }}
      </h4>

      <v-row align="end">
        <v-col cols="6" sm="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('contract.details.hostTax') }}
          </h6>
          <span class="body-2">{{ defaultServiceValue.host_tax }}%</span>
        </v-col>
        <v-col cols="6" sm="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('contract.details.hostRealtyFeatureTax') }}
          </h6>
          <span class="body-2">
            {{ defaultServiceValue.host_realty_feature_tax }}%
          </span>
        </v-col>
        <v-col cols="6" sm="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('contract.details.displacementTax') }}
          </h6>
          <span class="body-2">
            {{ defaultServiceValue.displacement_tax }}%
          </span>
        </v-col>
        <v-col cols="6" sm="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('contract.details.squareMetersTax') }}
          </h6>
          <span class="body-2">
            {{ defaultServiceValue.square_meter_tax }}%
          </span>
        </v-col>
        <v-col cols="6" sm="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('contract.details.furnitureTax') }}
          </h6>
          <span class="body-2">{{ defaultServiceValue.furniture_tax }}%</span>
        </v-col>
        <v-col cols="6" sm="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('contract.details.semiFurnitureTax') }}
          </h6>
          <span class="body-2" v-if="defaultServiceValue.semi_furniture_tax">{{ defaultServiceValue.semi_furniture_tax }}%</span>
          <span class="body-2" v-else>---</span>
        </v-col>
        <v-col cols="6" sm="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('contract.details.houseTax') }}
          </h6>
          <span class="body-2" v-if="defaultServiceValue.house_tax">{{ defaultServiceValue.house_tax }}%</span>
          <span class="body-2" v-else>---</span>
        </v-col>
        <v-col cols="6" sm="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('contract.details.trousseauTax') }}
          </h6>
          <span class="body-2">{{ defaultServiceValue.trousseau_tax }}%</span>
        </v-col>
        <v-col v-if="defaultServiceValue.urgency_tax" cols="6" sm="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('contract.details.urgencyTax') }}
          </h6>
          <span class="body-2">{{ defaultServiceValue.urgency_tax }}%</span>
        </v-col>
        <v-col v-if="defaultServiceValue.urgency_tax && defaultServiceValue.urgency_tax > 0 && defaultServiceValue.host_urgency_tax" cols="6" sm="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('contract.details.hostUrgencyTax') }}
          </h6>
          <span class="body-2">{{ defaultServiceValue.host_urgency_tax }}%</span>
        </v-col>
        <v-col v-if="defaultServiceValue.urgency_tax && defaultServiceValue.urgency_tax > 0 && defaultServiceValue.urgency_hours_limit" cols="6" sm="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('contract.details.urgencyHoursLimit') }}
          </h6>
          <span class="body-2">{{ defaultServiceValue.urgency_hours_limit }}h</span>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { catalogMonetary } from '@/util/catalog'
import { mapGetters } from 'vuex'

export default {
  name: 'DefaultServiceValuesDetails',

  computed: {
    ...mapGetters(['defaultServiceValue', 'isDefaultServiceValueLoading']),
  },

  data() {
    return {
      formatMoney: catalogMonetary,
    }
  },
}
</script>
